import { themeSchema, type Theme } from '@xphoto/shared';

/**
 * Clé de persistance du thème. **Doit rester synchronisée** avec le script
 * inline anti-flash de `index.html` (qui lit la même clé avant le premier paint).
 */
export const THEME_STORAGE_KEY = 'xphoto.theme';

/** Défaut applicatif imposé par STACK.md §7. */
export const DEFAULT_THEME: Theme = 'system';

/** Media query « préférence système = sombre ». */
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Lit la préférence persistée, ou `null` si absente/invalide/indisponible. */
export function readStoredTheme(): Theme | null {
  try {
    const parsed = themeSchema.safeParse(localStorage.getItem(THEME_STORAGE_KEY));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Préférence initiale : persistée sinon `system`. */
export function detectInitialTheme(): Theme {
  return readStoredTheme() ?? DEFAULT_THEME;
}

/** `true` si le système est actuellement en mode sombre. */
export function systemPrefersDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia(DARK_QUERY).matches;
}

/** Résout le thème effectif (`light`/`dark`) à partir de la préférence. */
export function resolveEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

/** Applique/retire la classe `dark` sur `<html>` selon la préférence. */
export function applyTheme(theme: Theme): void {
  const dark = resolveEffectiveTheme(theme) === 'dark';
  document.documentElement.classList.toggle('dark', dark);
}

/** Persiste la préférence (silencieux si `localStorage` indisponible). */
export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* stockage indisponible : on continue sans persistance */
  }
}

/**
 * S'abonne aux changements système ; appelle `onChange` uniquement utile quand
 * la préférence courante est `system`. Renvoie une fonction de désabonnement.
 */
export function subscribeSystemTheme(onChange: () => void): () => void {
  if (typeof matchMedia === 'undefined') return () => {};
  const mql = matchMedia(DARK_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}
